import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Session, User } from '@supabase/supabase-js';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isAuthReady: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  isAuthReady: false,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    // DEBUG: Log hash on mount to verify OAuth callback data arrives
    console.log('[AuthProvider] URL Hash on mount:', window.location.hash);
    console.log('[AuthProvider] Full URL:', window.location.href);

    // CRITICAL: Set up listener BEFORE checking session
    // This ensures we catch any auth state changes including OAuth callbacks
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[AuthProvider] Auth event:', event, 'Session:', !!session, 'User:', session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);
        setIsAuthReady(true);
        // NOTE: Redirect logic moved to AuthCallback.tsx to prevent race conditions
      }
    );

    // THEN: Check for existing session
    // This handles both the OAuth hash fragment AND existing persisted sessions
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('[AuthProvider] Initial session:', !!session, session?.user?.email);
      setSession(session);
      setUser(session?.user ?? null);
      setIsAuthReady(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
  };

  // Block rendering until auth is resolved
  // This prevents race conditions where the app renders before OAuth is processed
  if (!isAuthReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Verificando sessão...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ session, user, isAuthReady, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
